import { useState } from 'react'
import { useAuth } from '../lib/AuthContext'

const C = {
  black: "#0D0D0D", white: "#FAFAF8", orange: "#E8622A", orangeLight: "#F07A45",
  orangeFaint: "#FDF0E8", ink: "#1A1A1A", inkMid: "#3D3D3D", inkLight: "#777",
  border: "#E8E4DE", surface: "#F2EEE8",
}

const FONT = { display: "'Bebas Neue', sans-serif", body: "'DM Sans', sans-serif" }

export default function AuthPage() {
  const [mode, setMode] = useState('login')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const { signIn, signUp } = useAuth()

  const handleSubmit = async () => {
    setError('')
    setMessage('')
    if (!email || !password) { setError('Please fill in all fields'); return }
    if (mode === 'signup' && !name) { setError('Please enter your name'); return }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return }
    setLoading(true)
    if (mode === 'login') {
      const { error } = await signIn(email, password)
      if (error) setError(error.message)
    } else {
      const { error } = await signUp(email, password, name)
      if (error) setError(error.message)
      else setMessage('Check your email for a confirmation link!')
    }
    setLoading(false)
  }

  const inputStyle = {
    width: '100%', border: `1px solid ${C.border}`, padding: '12px 14px',
    fontSize: 14, fontFamily: FONT.body, outline: 'none',
    color: C.ink, background: C.surface, boxSizing: 'border-box',
    borderRadius: 0,
  }

  const labelStyle = {
    fontSize: 10, fontWeight: 700, color: C.inkLight,
    display: 'block', marginBottom: 6,
    fontFamily: FONT.body, letterSpacing: '0.08em', textTransform: 'uppercase',
  }

  return (
    <div style={{ minHeight: '100vh', background: C.white, display: 'flex', fontFamily: FONT.body }}>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* Left panel — branding */}
      <div style={{ width: '45%', background: C.black, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '48px 48px', position: 'relative', overflow: 'hidden' }}>
        {/* Grid pattern */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `repeating-linear-gradient(0deg, #E8622A08 0, #E8622A08 1px, transparent 0, transparent 60px), repeating-linear-gradient(90deg, #E8622A08 0, #E8622A08 1px, transparent 0, transparent 60px)` }} />

        {/* Logo */}
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 60 }}>
            <div style={{ width: 8, height: 8, background: C.orange, borderRadius: '50%' }} />
            <span style={{ fontFamily: FONT.display, fontSize: 24, color: C.white, letterSpacing: '0.14em' }}>EDIFYI</span>
          </div>

          {/* Big headline */}
          <div style={{ fontFamily: FONT.display, fontSize: 72, color: C.white, lineHeight: 0.9, letterSpacing: '0.02em', marginBottom: 24 }}>
            EDUCATION<br />IS<br /><span style={{ color: C.orange }}>YOURS.</span>
          </div>

          <p style={{ color: '#666', fontSize: 15, lineHeight: 1.7, fontFamily: FONT.body, maxWidth: 320 }}>
            The professional network where every educator is seen, connected and empowered to flourish.
          </p>
        </div>

        {/* Acronym at bottom */}
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {[
              ['E', 'Empowering'],
              ['D', 'Discovery'],
              ['I', 'Inspiring'],
              ['F', 'Flourishing'],
              ['Y', 'Yours'],
              ['I', 'Inclusive'],
            ].map(([letter, word]) => (
              <div key={word} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontFamily: FONT.display, fontSize: 18, color: C.orange, letterSpacing: '0.1em', width: 16 }}>{letter}</span>
                <span style={{ fontSize: 12, color: '#444', fontFamily: FONT.body, letterSpacing: '0.04em' }}>{word}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 64px', background: C.white }}>
        <div style={{ width: '100%', maxWidth: 400 }}>

          {/* Tab switcher */}
          <div style={{ display: 'flex', marginBottom: 36, borderBottom: `2px solid ${C.border}` }}>
            {['login', 'signup'].map(m => (
              <button key={m} onClick={() => { setMode(m); setError(''); setMessage('') }} style={{
                flex: 1, padding: '14px 0',
                background: 'none', border: 'none',
                borderBottom: `2px solid ${mode === m ? C.orange : 'transparent'}`,
                marginBottom: -2,
                color: mode === m ? C.orange : C.inkLight,
                fontFamily: FONT.display, fontSize: 20,
                letterSpacing: '0.08em', cursor: 'pointer',
                transition: 'all 0.2s',
              }}>
                {m === 'login' ? 'LOG IN' : 'SIGN UP'}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {mode === 'signup' && (
              <div>
                <label style={labelStyle}>Your Name</label>
                <input value={name} onChange={e => setName(e.target.value)}
                  placeholder="e.g. Alex Thompson" style={inputStyle} />
              </div>
            )}
            <div>
              <label style={labelStyle}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@school.com" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Min. 6 characters"
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                style={inputStyle} />
            </div>
          </div>

          {error && (
            <div style={{ marginTop: 14, background: '#FEE2E2', border: '1px solid #FECACA', padding: '10px 14px', fontSize: 13, color: '#DC2626', fontFamily: FONT.body }}>
              {error}
            </div>
          )}
          {message && (
            <div style={{ marginTop: 14, background: '#DCFCE7', border: '1px solid #BBF7D0', padding: '10px 14px', fontSize: 13, color: '#166534', fontFamily: FONT.body }}>
              {message}
            </div>
          )}

          <button onClick={handleSubmit} disabled={loading} style={{
            width: '100%', marginTop: 24, padding: '16px 0',
            background: loading ? C.inkLight : C.orange,
            border: 'none', color: '#fff',
            fontFamily: FONT.display, fontSize: 22,
            letterSpacing: '0.08em', cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
          }}>
            {loading ? 'PLEASE WAIT...' : mode === 'login' ? 'LOG IN →' : 'JOIN EDIFYI →'}
          </button>

          {mode === 'signup' && (
            <p style={{ textAlign: 'center', fontSize: 11, color: C.inkLight, marginTop: 16, lineHeight: 1.6, fontFamily: FONT.body, letterSpacing: '0.02em' }}>
              By signing up you agree to our Terms of Service.<br />Edifyi is free for individual educators.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
